/** User-facing fallback when the API is unreachable or returns non-JSON (e.g. HTML). */
export const FRIENDLY_SERVER_ERROR_MESSAGE =
  'Um erro ocorreu no servidor. Tente novamente mais tarde.';

/**
 * Detect browser / fetch JSON parse failures and HTML error bodies that must not be shown raw.
 * @param {unknown} message
 * @returns {boolean}
 */
export function isTechnicalApiErrorMessage(message) {
  if (message == null) return false;
  const text = String(message).trim();
  if (!text) return false;

  const lower = text.toLowerCase();
  return (
    lower.includes('unexpected token') ||
    lower.includes('is not valid json') ||
    lower.includes('failed to execute \'json\'') ||
    lower.includes('unexpected end of json') ||
    lower.startsWith('<!doctype') ||
    lower.startsWith('<html') ||
    (lower.includes('syntaxerror') && lower.includes('json'))
  );
}

/**
 * Build a single user-facing string from API error payloads (JSON body, Error, or string).
 *
 * @param {unknown} err
 * @param {string} [fallback] — used when nothing readable is found
 * @returns {string}
 */
export function formatApiErrorMessage(err, fallback = '') {
  const defaultFallback = fallback || FRIENDLY_SERVER_ERROR_MESSAGE;

  if (err == null || err === '') {
    return defaultFallback;
  }

  if (typeof err === 'string') {
    const t = err.trim();
    if (!t) return defaultFallback;
    if (isTechnicalApiErrorMessage(t)) return FRIENDLY_SERVER_ERROR_MESSAGE;
    return t;
  }

  if (err instanceof Error) {
    const t = (err.message || '').trim();
    if (!t) return defaultFallback;
    if (isTechnicalApiErrorMessage(t)) return FRIENDLY_SERVER_ERROR_MESSAGE;
    return t;
  }

  if (typeof err !== 'object') {
    const asString = String(err);
    if (isTechnicalApiErrorMessage(asString)) return FRIENDLY_SERVER_ERROR_MESSAGE;
    return asString;
  }

  if (err.__htmlResponse) {
    return FRIENDLY_SERVER_ERROR_MESSAGE;
  }

  const tryString = (v) =>
    typeof v === 'string' && v.trim() ? v.trim() : '';

  const fromDetailArray = (detail) => {
    if (!Array.isArray(detail)) return '';
    const parts = detail
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (item && typeof item === 'object') {
          return tryString(item.msg) || tryString(item.message) || '';
        }
        return '';
      })
      .filter(Boolean);
    return parts.length ? parts.join(' ') : '';
  };

  const detail = err.detail;
  if (Array.isArray(detail)) {
    const fromArr = fromDetailArray(detail);
    if (fromArr) {
      return isTechnicalApiErrorMessage(fromArr)
        ? FRIENDLY_SERVER_ERROR_MESSAGE
        : fromArr;
    }
  }
  if (typeof detail === 'string' && detail.trim()) {
    const t = detail.trim();
    return isTechnicalApiErrorMessage(t) ? FRIENDLY_SERVER_ERROR_MESSAGE : t;
  }

  for (const key of ['message', 'error', 'title', 'description']) {
    const s = tryString(err[key]);
    if (s) {
      return isTechnicalApiErrorMessage(s) ? FRIENDLY_SERVER_ERROR_MESSAGE : s;
    }
  }

  if (err.errors && typeof err.errors === 'object') {
    const msgs = [];
    for (const v of Object.values(err.errors)) {
      if (typeof v === 'string' && v.trim()) msgs.push(v.trim());
      else if (Array.isArray(v)) {
        for (const x of v) {
          if (typeof x === 'string' && x.trim()) msgs.push(x.trim());
        }
      }
    }
    if (msgs.length) {
      const joined = msgs.join(' ');
      return isTechnicalApiErrorMessage(joined)
        ? FRIENDLY_SERVER_ERROR_MESSAGE
        : joined;
    }
  }

  if (fallback) return fallback;
  if (Object.keys(err).length === 0) return FRIENDLY_SERVER_ERROR_MESSAGE;
  try {
    const serialized = JSON.stringify(err);
    if (isTechnicalApiErrorMessage(serialized)) {
      return FRIENDLY_SERVER_ERROR_MESSAGE;
    }
    return serialized;
  } catch {
    return FRIENDLY_SERVER_ERROR_MESSAGE;
  }
}
